/* eslint-disable max-statements */

import { Autocomplete, AutocompleteProps, Loader } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import React, { useEffect, useMemo, useState } from 'react';

import { DEBOUNCE_TIME } from '@constants';

export type AutocompleteItemType = string | { value?: string; label?: string; [key: string]: any };

export interface AsyncAutocompleteProps extends Omit<AutocompleteProps, 'data'> {
  fetchOptions?: (query: string) => Promise<AutocompleteItemType[]>;
  data?: AutocompleteItemType[];
  debounceTime?: number;
  minChars?: number;
  initialOptions?: AutocompleteItemType[];
  errorMessage?: string;
  'data-pw'?: string;
}

export const AsyncAutocomplete: React.FC<AsyncAutocompleteProps> = ({
  fetchOptions,
  data = [],
  debounceTime = DEBOUNCE_TIME,
  minChars = 1,
  initialOptions = [],
  errorMessage = 'Failed to load options',
  onChange,
  onFocus,
  onBlur,
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const [query, setQuery] = useState(props.value ?? '');
  const [options, setOptions] = useState<AutocompleteItemType[]>(fetchOptions ? initialOptions : data);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const floating = useMemo(() => {
    const value = ['number', 'string'].includes(typeof query) ? String(query).trim() : '';
    return value.length !== 0 || focused ? true : undefined;
  }, [query, focused]);

  const getItemLabel = (item: AutocompleteItemType): string => {
    if (typeof item === 'string') {
      return item;
    }
    return item.label;
  };

  const filterItems = (items: AutocompleteItemType[], searchQuery: string): AutocompleteItemType[] => {
    const lowercaseQuery = searchQuery.toLowerCase();
    return items.filter((item) => {
      const label = getItemLabel(item);
      return label.toLowerCase().includes(lowercaseQuery);
    });
  };

  const debouncedQuery = useDebouncedCallback(async (query: string) => {
    if (!fetchOptions) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fetchOptions(query);
      setOptions(result);
    } catch (err) {
      setError(errorMessage);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, debounceTime);

  // Update options when data prop changes (for non-async mode)
  useEffect(() => {
    if (!fetchOptions) {
      setOptions(data);
    }
  }, [data, fetchOptions]);

  useEffect(() => {
    setError(props.error as string);
  }, [props.error]);

  const handleChange = (value: string) => {
    setQuery(value);

    if (!fetchOptions) {
      const filteredOptions = filterItems(data, value);
      setOptions(filteredOptions);
    }

    const selectedItem = options.find((item) => getItemLabel(item) === value);

    if (onChange) {
      if (selectedItem && typeof selectedItem !== 'string') {
        onChange(selectedItem.value);
      } else {
        onChange(value);
      }
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    // If no query and in async mode with initial options, load them
    if (fetchOptions && !query && initialOptions.length > 0) {
      setOptions(initialOptions);
    }

    // In non-async mode, restore all options when focusing with empty query
    if (!fetchOptions && !query) {
      setOptions(data);
    }

    onFocus?.(event);
    setFocused(true);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.(event);
    setFocused(false);
  };

  useEffect(() => {
    if (props.value) {
      setQuery(props.value);
    }
  }, [props.value]);

  useEffect(() => {
    if (focused) {
      debouncedQuery(query || '');
    }
  }, [query, focused]);

  const processedData = options.map((item) => {
    if (typeof item === 'string') {
      return item;
    }
    return item.label;
  });

  return (
    <Autocomplete
      data-pw={props['data-pw']}
      {...props}
      value={query}
      data={processedData}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      rightSection={loading ? <Loader size="xs" /> : props.rightSection}
      error={error}
      data-floating={floating}
      labelProps={{ ...props.labelProps, 'data-floating': floating, 'data-error': error }}
    />
  );
};
